import type {
  BulkUpdateTimeEntriesInput,
  BulkUpdateTimeEntriesResult,
  CreateTimeEntryRequest,
} from '@/lib/api/time-entries.api'
import { getMutationErrorMessage } from '@/lib/api/mutation-feedback'
import { createLogger } from '@/lib/logger'
import { timeEntryApi } from '@/modules/time'
/**
 * Time Entry mutation hooks using factory pattern with timer-specific operations
 * Reduced from 185 lines to ~100 lines (46% reduction)
 * All features preserved: CRUD, pause/resume, heartbeat
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
  SplitTimeEntryInput,
  SwitchTimerItemInput,
  TimeEntry,
  UpdateTimeEntryInput,
} from '@scrambl/shared'

import { invalidateTimeFinancialViews } from '../queries/invalidateTimeFinancialViews'
import { payPeriodKeys } from '../queries/usePayPeriodsQuery'
import { queryKeys } from '../query-keys'
import { TODAY_TIME_STATS_QUERY_KEY } from '../useTodayTimeStats'
import { createResourceMutations } from './createResourceMutations'

const logger = createLogger('hooks/mutations/useTimeEntryMutations')

// Helper to invalidate time entry related queries
function invalidateTimeEntryQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all })
  queryClient.invalidateQueries({ queryKey: payPeriodKeys.all })
  // Home's Time today card keeps its own key outside timeEntries.all; without
  // this it kept counting a deleted or stopped timer until it remounted.
  queryClient.invalidateQueries({ queryKey: TODAY_TIME_STATS_QUERY_KEY })
}

// Core CRUD mutations using factory
const timeEntryMutations = createResourceMutations<
  TimeEntry,
  CreateTimeEntryRequest,
  UpdateTimeEntryInput
>({
  resourceName: 'Time entry',
  resourceNameSingular: 'timeEntry',
  queryKeyFactory: queryKeys.timeEntries,
  api: {
    create: (data: CreateTimeEntryRequest) =>
      timeEntryApi.createTimeEntry(data),
    update: (id: string, data: UpdateTimeEntryInput) =>
      timeEntryApi.updateTimeEntry(id, data),
    delete: (id: string) => timeEntryApi.deleteTimeEntry(id),
  },
  invalidation: {
    onCreate: {
      additionalInvalidations: [
        () => queryKeys.timeEntries.all,
        () => payPeriodKeys.all,
        () => TODAY_TIME_STATS_QUERY_KEY,
      ],
    },
    onUpdate: {
      additionalInvalidations: [
        () => queryKeys.timeEntries.all,
        () => payPeriodKeys.all,
        () => TODAY_TIME_STATS_QUERY_KEY,
      ],
    },
    onDelete: {
      additionalInvalidations: [
        () => queryKeys.timeEntries.all,
        () => payPeriodKeys.all,
        () => TODAY_TIME_STATS_QUERY_KEY,
      ],
    },
  },
  enableRealtime: false,
})

export const useCreateTimeEntryMutation = timeEntryMutations.useCreateMutation
export const useUpdateTimeEntryMutation = timeEntryMutations.useUpdateMutation
export const useDeleteTimeEntryMutation = timeEntryMutations.useDeleteMutation

export function useVerifyTimeEntryCostCurrencyMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string
      expectedVersion: number
      currency: string
      sourceReference: string
      reason: string
    }) => timeEntryApi.verifyCostCurrency(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all })
      invalidateTimeFinancialViews(queryClient)
      toast.success('Cost currency recorded with its source')
    },
    onError: error => {
      toast.error(
        getMutationErrorMessage(error, "Couldn't record the cost currency."),
      )
    },
  })
}

export function useVerifyTimeEntryBillCurrencyMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string
      expectedVersion: number
      currency: string
      sourceReference: string
      reason: string
    }) => timeEntryApi.verifyBillCurrency(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all })
      invalidateTimeFinancialViews(queryClient)
      toast.success('Bill currency recorded with its source')
    },
    onError: error => {
      toast.error(
        getMutationErrorMessage(error, "Couldn't record the bill currency."),
      )
    },
  })
}

// Timer-specific mutations
export function usePauseTimerMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => timeEntryApi.pauseTimer(id),
    onSuccess: updatedTimeEntry => {
      queryClient.setQueryData(
        queryKeys.timeEntries.detail(updatedTimeEntry.id),
        updatedTimeEntry,
      )
      invalidateTimeEntryQueries(queryClient)
      toast.success('Timer paused')
    },
    onError: error => {
      logger.error('[usePauseTimerMutation] Error:', error)
      toast.error(getMutationErrorMessage(error, "Couldn't pause the timer."))
    },
  })
}

export function useResumeTimerMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => timeEntryApi.resumeTimer(id),
    onSuccess: updatedTimeEntry => {
      queryClient.setQueryData(
        queryKeys.timeEntries.detail(updatedTimeEntry.id),
        updatedTimeEntry,
      )
      invalidateTimeEntryQueries(queryClient)
      toast.success('Timer resumed')
    },
    onError: error => {
      logger.error('[useResumeTimerMutation] Error:', error)
      toast.error(getMutationErrorMessage(error, "Couldn't resume the timer."))
    },
  })
}

export function useUpdateHeartbeatMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => timeEntryApi.updateHeartbeat(id),
    onSuccess: result => {
      // If the timer is no longer active on the server, invalidate queries
      // to refresh state and clear the stale timer
      if (!result.active) {
        invalidateTimeEntryQueries(queryClient)
      }
    },
    onError: error => {
      logger.error('[useUpdateHeartbeatMutation] Error:', error)
      // No toast for heartbeat errors - background operation
    },
  })
}

export function useSplitTimeEntryMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SplitTimeEntryInput }) =>
      timeEntryApi.splitTimeEntry(id, data),
    onSuccess: () => {
      invalidateTimeEntryQueries(queryClient)
      toast.success('Time entry split')
    },
    onError: error => {
      logger.error('[useSplitTimeEntryMutation] Error:', error)
      toast.error(
        getMutationErrorMessage(error, "Couldn't split the time entry."),
      )
    },
  })
}

export function useSwitchTimerItemMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SwitchTimerItemInput }) =>
      timeEntryApi.switchTimerItem(id, data),
    onSuccess: updatedTimeEntry => {
      queryClient.setQueryData(
        queryKeys.timeEntries.detail(updatedTimeEntry.id),
        updatedTimeEntry,
      )
      invalidateTimeEntryQueries(queryClient)
    },
    onError: error => {
      logger.error('[useSwitchTimerItemMutation] Error:', error)
      toast.error(
        getMutationErrorMessage(error, "Couldn't switch the timer item."),
      )
    },
  })
}

/**
 * "Updated 3 · skipped 1 (approved or invoiced)" — the API skips entries it
 * won't reclassify, so the toast always reports both halves when any skip.
 */
export function formatBulkUpdateSummary(
  result: BulkUpdateTimeEntriesResult,
): string {
  const updated = result.updated.length
  const skipped = result.skipped.length
  if (skipped === 0) {
    return `Updated ${updated} ${updated === 1 ? 'entry' : 'entries'}`
  }
  return `Updated ${updated} · skipped ${skipped} (approved or invoiced)`
}

/** Bulk reclassify (billable, time category) for a selection of entries. */
export function useBulkUpdateTimeEntriesMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: BulkUpdateTimeEntriesInput) =>
      timeEntryApi.bulkUpdateTimeEntries(input),
    onSuccess: result => {
      invalidateTimeEntryQueries(queryClient)
      if (result.updated.length === 0 && result.skipped.length > 0) {
        toast.warning(formatBulkUpdateSummary(result))
      } else {
        toast.success(formatBulkUpdateSummary(result))
      }
    },
    onError: error => {
      logger.error('[useBulkUpdateTimeEntriesMutation] Error:', error)
      toast.error(getMutationErrorMessage(error, "Couldn't update entries."))
    },
  })
}
